import { Badge, Container, SectionHeader, Tabs } from "@lumberset/ui";
import { InformationCircle } from "@medusajs/icons";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Crumbs } from "../../components/crumbs";
import { settingsQuery } from "../../lib/queries";
import { pendingPricesQuery, priceBookQuery } from "../../lib/queries-prices";
import { ApprovalsTab } from "./reference-prices/-approvals";
import { BookTab } from "./reference-prices/-book";
import { ImportTab } from "./reference-prices/-import";
import { MarketTab } from "./reference-prices/-market";

const tabs = ["book", "approvals", "market", "import"] as const;
type Tab = (typeof tabs)[number];

export const Route = createFileRoute("/_app/reference-prices")({
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } =>
    tabs.includes(search.tab as Tab) && search.tab !== "book" ? { tab: search.tab as Tab } : {},
  loader: ({ context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(priceBookQuery({ region: "US" })),
      queryClient.ensureQueryData(pendingPricesQuery),
    ]),
  component: ReferencePrices,
});

/**
 * The reference price book: where every Budget Estimate's prices come from.
 * Staff propose in the Staff panel; here an Admin approves, edits, imports
 * price sheets and moves prices with the market.
 */
function ReferencePrices() {
  const { tab = "book" } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: pending } = useQuery(pendingPricesQuery);
  const { data: settings } = useQuery(settingsQuery);
  const reviewed = settings?.settings["pricebook.reviewed"] === true;
  const show = (next: Tab) =>
    navigate({ search: next === "book" ? {} : { tab: next }, replace: true });

  return (
    <>
      <Crumbs trail={[{ label: "Reference prices" }]} />
      <Tabs
        value={tab}
        onValueChange={(value) => show(value as Tab)}
        className="flex flex-col gap-y-3"
      >
        <Container className="divide-y p-0">
          <SectionHeader
            level="h1"
            title="Reference prices"
            description="The price book behind every Builder's Budget Estimate: a budget guide, never a quote. Each price keeps where it came from, who set it and when."
          />
          {settings && !reviewed ? (
            <div className="txt-small flex items-start gap-x-2 px-6 py-3 text-ui-fg-subtle">
              <InformationCircle className="mt-0.5 shrink-0 text-ui-fg-muted" />
              <p>
                Budget Estimates tell Builders the book is still being reviewed. Once you have
                checked it, switch on "The price book has been reviewed" in{" "}
                <Link to="/settings" className="text-ui-fg-interactive hover:underline">
                  Settings
                </Link>
                .
              </p>
            </div>
          ) : null}
          <div className="px-6 py-3">
            <Tabs.List>
              <Tabs.Trigger value="book">Price book</Tabs.Trigger>
              <Tabs.Trigger value="approvals">
                Approvals
                {pending && pending.pending > 0 ? (
                  <Badge color="blue" rounded="full">
                    {pending.pending}
                  </Badge>
                ) : null}
              </Tabs.Trigger>
              <Tabs.Trigger value="market">Market</Tabs.Trigger>
              <Tabs.Trigger value="import">Import</Tabs.Trigger>
            </Tabs.List>
          </div>
        </Container>

        <Tabs.Content value="book" className="flex flex-col gap-y-3">
          <BookTab onShowApprovals={() => show("approvals")} />
        </Tabs.Content>
        <Tabs.Content value="approvals" className="flex flex-col gap-y-3">
          <ApprovalsTab />
        </Tabs.Content>
        <Tabs.Content value="market" className="flex flex-col gap-y-3">
          <MarketTab />
        </Tabs.Content>
        <Tabs.Content value="import" className="flex flex-col gap-y-3">
          <ImportTab />
        </Tabs.Content>
      </Tabs>
    </>
  );
}
